import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { EditPharmacy, apiErrorMessage } from '../edit/editApi';
import { getCurrentMonthStr, getPrevMonthStr, getTodayStr, isYmd } from '../edit/editCalc';

/** Базовый адрес сводного отчета (все запросы проверяются сервером по сессии и правам роли) */
export const SUMMARY_API = '/api/v2/reports/summary';

/** Самая ранняя дата, для которой имеет смысл запрашивать отчет (отсекает промежуточный ввод года в поле даты) */
export const MIN_REPORT_DATE = '2000-01-01';

export interface SummaryField {
  name: string;
  label: string;
  /** Колонка входит в итоговую строку */
  summable: boolean;
  /** Группа шапки (Касса / Банк / Статистика), как в «Ред. мес. отчет»; пусто - без группы */
  group: string;
  /** Ключевое поле: в таблице выделено жирным */
  important: boolean;
}

/** Строка отчета: дата YYYY-MM-DD и числа по именам полей (null - значения нет) */
export interface SummaryRow {
  date: string;
  [field: string]: string | number | null;
}

export interface SummaryResponse {
  success: boolean;
  pharmacy: { username: string; pharmacyNum: string; fullName: string | null };
  startDate: string;
  endDate: string;
  fields: SummaryField[];
  rows: SummaryRow[];
  totals: Record<string, number>;
}

export type SummaryAuditKind = 'excel' | 'excel_custom' | 'cash' | 'bank';

export interface SummaryPeriod {
  startDate: string;
  endDate: string;
}

/** Период можно запрашивать: обе даты заданы, корректны и начало не позже конца */
export function isPeriodValid(startDate: string, endDate: string): boolean {
  return (
    isYmd(startDate) && isYmd(endDate) &&
    startDate >= MIN_REPORT_DATE && endDate >= MIN_REPORT_DATE &&
    startDate <= endDate
  );
}

function lastDayOfMonth(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  const day = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return `${ym}-${String(day).padStart(2, '0')}`;
}

/** Период «с начала текущего месяца по сегодня» */
export function currentMonthPeriod(): SummaryPeriod {
  return { startDate: `${getCurrentMonthStr()}-01`, endDate: getTodayStr() };
}

/** Период «весь прошлый месяц» */
export function prevMonthPeriod(): SummaryPeriod {
  const ym = getPrevMonthStr();
  return { startDate: `${ym}-01`, endDate: lastDayOfMonth(ym) };
}

/** Данные сводного отчета за период (silent - перезагрузка без новой записи в журнал) */
export async function fetchSummary(
  username: string,
  period: SummaryPeriod,
  silent: boolean
): Promise<SummaryResponse> {
  const res = await axios.get(SUMMARY_API, {
    params: { username, startDate: period.startDate, endDate: period.endDate, silent: silent ? 1 : undefined }
  });
  if (!res.data || !res.data.success) {
    throw new Error((res.data && res.data.message) || 'Не удалось загрузить отчет');
  }
  return res.data as SummaryResponse;
}

/** Запись в журнал о выгрузке (файл собирается в браузере); сбой журнала не мешает работе */
export async function auditSummary(kind: SummaryAuditKind, username: string, period: SummaryPeriod): Promise<void> {
  try {
    await axios.post(`${SUMMARY_API}/audit`, { kind, username, startDate: period.startDate, endDate: period.endDate });
  } catch (err) {
    console.error('Не удалось записать выгрузку в журнал:', apiErrorMessage(err, ''));
  }
}

export interface SummaryUsers {
  users: EditPharmacy[];
  scope: string;
}

/** Список аптек, доступных роли для сводного отчета (GET /api/v2/reports/users-for-summary) */
export function useSummaryUsers() {
  const [data, setData] = useState<SummaryUsers | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get('/api/v2/reports/users-for-summary');
      if (!res.data || !res.data.success) {
        setError((res.data && res.data.message) || 'Не удалось загрузить список аптек');
        return;
      }
      setData({ users: res.data.users || [], scope: res.data.scope || '' });
    } catch (err) {
      setError(apiErrorMessage(err, 'Не удалось загрузить список аптек'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return { data, loading, error, reload: load };
}

// ---------------------------------------------------------------------------
// Запомненный выбор (только фильтры: данные всегда запрашиваются заново у сервера)
// ---------------------------------------------------------------------------

const FILTERS_KEY = 'summaryReportFilters';
const LEGACY_CACHE_KEY = 'periodReportData';

export interface SavedFilters extends SummaryPeriod {
  username: string;
}

export function loadFilters(): SavedFilters | null {
  try {
    const raw = sessionStorage.getItem(FILTERS_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    if (v && typeof v.username === 'string' && typeof v.startDate === 'string' && typeof v.endDate === 'string') {
      return v as SavedFilters;
    }
  } catch (e) {
    // хранилище недоступно или повреждено - начинаем с чистого выбора
  }
  return null;
}

export function saveFilters(filters: SavedFilters | null): void {
  try {
    if (filters) sessionStorage.setItem(FILTERS_KEY, JSON.stringify(filters));
    else sessionStorage.removeItem(FILTERS_KEY);
  } catch (e) {
    // не критично: выбор просто не запомнится
  }
}

/** Прежняя версия кэшировала данные отчета в localStorage (они оставались в браузере после выхода) */
export function clearLegacyCache(): void {
  try {
    localStorage.removeItem(LEGACY_CACHE_KEY);
  } catch (e) {
    // не критично
  }
}
